// Pure logic for the Results tab (plan §6.5 item 4): which arms to show and
// in what order, the metric rows and their wording, when a value is marked,
// the first-audio bars, notices and the CSV. The numbers and their
// definitions are app/services/voice_engines/results.py's; null is "no
// data" and shows as "—", never as zero.

import type { ArmResults, RateRange, VoiceEngineResults } from "../../services/analytics";
import type { EngineName, Policy } from "../../services/voiceEngines";
import { armColor, engineLabel } from "./labels";
import { remainder } from "./policy";
import type { Tone } from "./parts";

export const DASH = "—";
// Colour of the rest (bucketed outside every arm, on the cascade).
export const REST_COLOR = "var(--muted)";
// The proposed experiment bar: first audio p50 at or under 2.0 s (§5.10).
export const FIRST_AUDIO_TARGET_MS = 2000;
// Where the first-audio bars end.
export const FIRST_AUDIO_SCALE_MS = 5000;

export interface ArmColumn {
  key: string | null; // null: the rest
  label: string;
  engine: EngineName | null;
  color: string;
  pct: number | null; // the arm's share in the policy, when the policy runs this experiment
  data: ArmResults | null; // null: no calls in this experiment yet
}

// The policy when it buckets calls now, else null. With the policy off or
// the kill switch on, every call runs the cascade outside any experiment
// (run_config.decide_voice_engine), whatever experiment the value names.
export function livePolicy(policy: Policy | null, s2sEnabled: boolean): Policy | null {
  return policy && policy.enabled && s2sEnabled && policy.experiment ? policy : null;
}

// The live policy's arms when it runs the experiment shown, else none.
function liveArms(results: VoiceEngineResults, policy: Policy | null): Policy["arms"] {
  return policy && results.experiment && policy.experiment === results.experiment ? policy.arms : [];
}

// Columns: the live policy's arms in its order (with or without calls), then
// arms only the calls know (removed since), then the rest. `policy` is
// livePolicy(...): a policy that is off shows no shares.
export function armColumns(results: VoiceEngineResults, policy: Policy | null): ArmColumn[] {
  const arms = liveArms(results, policy);
  const byKey = new Map(results.arms.map((a) => [a.arm, a]));
  const columns: ArmColumn[] = arms.map((arm, i) => {
    const data = byKey.get(arm.key) ?? null;
    return { key: arm.key, label: arm.key, engine: data?.engine ?? arm.engine, color: armColor(i), pct: arm.pct, data };
  });
  results.arms
    .filter((a) => a.arm !== null && !arms.some((p) => p.key === a.arm))
    .forEach((a, j) => {
      columns.push({ key: a.arm, label: a.arm ?? "", engine: a.engine, color: armColor(arms.length + j), pct: null, data: a });
    });
  const rest = byKey.get(null) ?? null;
  const restPct = arms.length ? remainder(arms) : null;
  if (rest || (restPct !== null && restPct > 0)) {
    columns.push({ key: null, label: "rest", engine: "cascade", color: REST_COLOR, pct: restPct, data: rest });
  }
  return columns;
}

// ---------------------------------------------------------------- formatting

export function pct(value: number | null | undefined): string {
  return value === null || value === undefined ? DASH : `${Math.round(value * 100)}%`;
}

// "41–68%"
export function pctRange(range: RateRange | null | undefined): string | null {
  return range ? `${Math.round(range.low * 100)}–${Math.round(range.high * 100)}%` : null;
}

// "0.68 s", "12.4 s"
export function seconds(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return DASH;
  const s = ms / 1000;
  return `${s.toFixed(s < 10 ? 2 : 1)} s`;
}

export function minutes(value: number | null | undefined): string {
  return value === null || value === undefined ? DASH : `${value.toFixed(1)} min`;
}

export function perCall(value: number | null | undefined): string {
  return value === null || value === undefined ? DASH : value.toFixed(2);
}

export function perCallRange(range: RateRange | null | undefined): string | null {
  return range ? `${range.low.toFixed(2)}–${range.high.toFixed(2)}` : null;
}

export function dollarsPerMinute(value: number | null | undefined): string {
  return value === null || value === undefined ? DASH : `$${value.toFixed(3)}/min`;
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

// ---------------------------------------------------------------- metrics

export type Better = "higher" | "lower";

export interface Cell {
  text: string;
  sub: string | null;
  danger?: boolean;
}

export interface Metric {
  key: string;
  label: string;
  hint: string;
  // Rows a value can be marked on: which way is better, the per-user value
  // and its range.
  better?: Better;
  value?: (a: ArmResults) => number | null;
  range?: (a: ArmResults) => RateRange | null;
  cell: (a: ArmResults | null, column: ArmColumn) => Cell;
}

const empty = (text = DASH): Cell => ({ text, sub: null });

export function metrics(longCallSeconds: number): Metric[] {
  return [
    {
      key: "users",
      label: "Users",
      hint: "the unit of analysis",
      cell: (a) => ({ text: String(a?.users ?? 0), sub: null }),
    },
    {
      key: "calls",
      label: "Spoken calls",
      hint: "reconnects folded into one call",
      cell: (a) => ({ text: String(a?.calls ?? 0), sub: a ? plural(a.connections, "socket") : null }),
    },
    {
      key: "longCalls",
      label: `Calls lasting ${longCallSeconds} s+`,
      hint: "each user's share, averaged · 95% range",
      better: "higher",
      value: (a) => a.longCallRate.value,
      range: (a) => a.longCallRate.ci,
      cell: (a) => (a ? { text: pct(a.longCallRate.value), sub: pctRange(a.longCallRate.ci) } : empty()),
    },
    {
      key: "median",
      label: "Median call length",
      hint: "median of each user's median",
      cell: (a) => (a ? { text: minutes(a.medianCallMinutes), sub: null } : empty()),
    },
    {
      key: "firstAudioP50",
      label: "First audio, p50",
      hint: "each socket's median, users weighed the same",
      cell: (a) => (a ? { text: seconds(a.firstAudio.p50Ms), sub: a.firstAudio.connections ? plural(a.firstAudio.connections, "socket") : null } : empty()),
    },
    {
      key: "firstAudioP95",
      label: "First audio, p95",
      hint: "the slow sockets",
      cell: (a) => (a ? { text: seconds(a.firstAudio.p95Ms), sub: null } : empty()),
    },
    {
      key: "interruptions",
      label: "Interruptions per call",
      hint: "replies the user cut off · 95% range",
      better: "lower",
      value: (a) => a.interruptionsPerCall.value,
      range: (a) => a.interruptionsPerCall.ci,
      cell: (a) => (a ? { text: perCall(a.interruptionsPerCall.value), sub: perCallRange(a.interruptionsPerCall.ci) } : empty()),
    },
    {
      key: "talkOvers",
      label: "Talk-overs per call",
      hint: "cut off before 15% was heard · 95% range",
      better: "lower",
      value: (a) => a.talkOversPerCall.value,
      range: (a) => a.talkOversPerCall.ci,
      cell: (a) => (a ? { text: perCall(a.talkOversPerCall.value), sub: perCallRange(a.talkOversPerCall.ci) } : empty()),
    },
    {
      key: "fallback",
      label: "Fell back to the cascade",
      hint: "each user's share of calls · reasons below",
      cell: (a, col) => {
        if (col.engine === "cascade") return empty("n/a");
        if (!a?.fallback) return empty();
        return { text: pct(a.fallback.rate.value), sub: a.calls ? `${a.fallback.calls} of ${a.calls}` : null };
      },
    },
    {
      key: "cost",
      label: "Cost per minute",
      hint: "recorded engine cost only",
      cell: (a, col) => {
        if (col.engine === "cascade") return empty("not recorded");
        if (!a?.cost) return empty();
        return { text: dollarsPerMinute(a.cost.perMinute), sub: `over ${Math.round(a.cost.minutes)} min` };
      },
    },
    {
      key: "sentiment",
      label: "Positive sentiment",
      hint: "each user's share of analyzed sockets · 95% range",
      better: "higher",
      value: (a) => a.sentiment.positiveShare.value,
      range: (a) => a.sentiment.positiveShare.ci,
      cell: (a) => (a ? { text: pct(a.sentiment.positiveShare.value), sub: pctRange(a.sentiment.positiveShare.ci) } : empty()),
    },
    {
      key: "crisis",
      label: "Crisis judge: missed or harmful",
      hint: "companion_safety_v1, flagged calls only",
      cell: (a) => {
        if (!a) return empty();
        const partial = a.crisis.partial ? ` · ${a.crisis.partial} partial` : "";
        return {
          text: String(a.crisis.missedOrHarmful),
          sub: `of ${a.crisis.judged} judged${partial}`,
          danger: a.crisis.missedOrHarmful > 0,
        };
      },
    },
  ];
}

// Two ranges are apart when they do not overlap.
function apart(a: RateRange, b: RateRange): boolean {
  return a.high < b.low || b.high < a.low;
}

// The column whose value is marked on a row, or -1. Marked only when at
// least two arms have the value, every one of them has `minUsers` users
// behind both the arm and the value, and the best value's 95% range
// overlaps none of the others'. A conservative reading of plain ranges,
// not a significance test.
export function markedColumn(columns: ArmColumn[], metric: Metric, minUsers: number): number {
  if (!metric.better || !metric.value || !metric.range) return -1;
  const { value, range } = metric;
  const candidates = columns
    .map((c, i) => ({ i, a: c.data }))
    .filter((x): x is { i: number; a: ArmResults } => x.a !== null && value(x.a) !== null);
  if (candidates.length < 2) return -1;
  for (const { a } of candidates) {
    const r = range(a);
    if (a.lowSample || !r || r.users < minUsers) return -1;
  }
  const sign = metric.better === "higher" ? 1 : -1;
  const best = candidates.reduce((x, y) => (sign * (value(y.a) as number) > sign * (value(x.a) as number) ? y : x));
  const bestRange = range(best.a) as RateRange;
  const clear = candidates.every((c) => c.i === best.i || apart(bestRange, range(c.a) as RateRange));
  return clear ? best.i : -1;
}

// ---------------------------------------------------------------- first audio

// Widths, in % of FIRST_AUDIO_SCALE_MS, of the p50 bar and the p50→p95 whisker.
export function firstAudioBar(
  firstAudio: ArmResults["firstAudio"] | null | undefined,
  scaleMs = FIRST_AUDIO_SCALE_MS,
): { p50: number; spread: number } | null {
  if (!firstAudio || firstAudio.p50Ms === null) return null;
  const at = (ms: number) => Math.min(100, Math.max(0, (ms / scaleMs) * 100));
  const p50 = at(firstAudio.p50Ms);
  const p95 = firstAudio.p95Ms === null ? p50 : at(firstAudio.p95Ms);
  return { p50, spread: Math.max(0, p95 - p50) };
}

// What each engine's first audio is measured from (results.py): not the
// same starting point, so the engines are not like for like.
export const FIRST_AUDIO_BASIS: Record<EngineName, string> = {
  cascade: "from Flux's end of turn, so the end-of-turn wait is left out",
  gemini_live: "from the user's last voiced audio, so Live's silence wait is in",
  elevenlabs: "from ElevenLabs' final transcript (recorded from plan B3 on)",
};

export const COST_BASIS: Record<EngineName, string> = {
  gemini_live: "Live's reported token usage at list price (live_cost.py)",
  elevenlabs: "ElevenLabs minutes (el_cost.py, plan B3); the text brain's tokens are not counted",
  cascade: "not recorded: Deepgram, ElevenLabs TTS and the text model's tokens are not logged per call",
};

// ---------------------------------------------------------------- links

// What a link to Calls must cover: the experiment's first to last call in
// every language (the Calls page has no language filter).
export interface CallsSpan {
  experiment: string;
  first: string | null;
  last: string | null;
}

export function callsSpan(results: VoiceEngineResults): CallsSpan | null {
  if (!results.experiment) return null;
  const seen = results.experiments.find((e) => e.key === results.experiment);
  return {
    experiment: results.experiment,
    first: seen?.firstCallAt ?? results.firstCallAt,
    last: seen?.lastCallAt ?? results.lastCallAt,
  };
}

// YYYY-MM-DD in local time, as the Calls page reads its date inputs.
function localDate(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// The Calls page, filtered to an arm's calls (and a fallback reason) over
// the experiment's span: without dates it opens on the last 30 days.
export function callsLink(span: CallsSpan, arm: string, fallback?: string): string {
  const params = new URLSearchParams({ experiment: span.experiment, arm });
  if (fallback) params.set("fallback", fallback);
  if (span.first) params.set("from", localDate(span.first));
  if (span.last) params.set("to", localDate(span.last));
  return `/calls?${params.toString()}`;
}

// ---------------------------------------------------------------- header

const LANGUAGE_LABEL: Record<string, string> = { en: "English", vi: "Vietnamese", unknown: "Unknown" };

export function languageLabel(language: string | null): string {
  if (language === null) return "All";
  return LANGUAGE_LABEL[language] ?? language;
}

// The language choices: All, then the experiment's languages, keeping the
// one picked even when this experiment has none of it.
export function languageOptions(results: VoiceEngineResults, picked: string | null): (string | null)[] {
  const seen = results.languages.map((l) => l.language);
  if (picked !== null && !seen.includes(picked)) seen.push(picked);
  return [null, ...seen];
}

// "9 days: 2 Nov to 10 Nov"; null with no calls.
export function spanText(first: string | null, last: string | null): string | null {
  if (!first || !last) return null;
  const a = new Date(first);
  const b = new Date(last);
  const days = Math.floor((b.getTime() - a.getTime()) / 86_400_000) + 1;
  const day = (d: Date) => d.toLocaleDateString("en-GB", { day: "numeric", month: "short", timeZone: "UTC" });
  return `${plural(days, "day")}: ${day(a)} to ${day(b)}`;
}

export interface Notice {
  tone: Tone;
  badge: string;
  text: string;
}

export function notices({
  results,
  columns,
  allowedEngines,
  environment,
  policy,
  s2sEnabled,
}: {
  results: VoiceEngineResults;
  columns: ArmColumn[];
  allowedEngines: string[];
  environment: string;
  policy: Policy | null; // the policy in force, on or off
  s2sEnabled: boolean;
}): Notice[] {
  const out: Notice[] = [];
  const min = results.lowSampleUsers;
  if (results.experiment && columns.length && columns.every((c) => !c.data?.calls)) {
    const where = results.language ? ` in ${languageLabel(results.language)}` : "";
    out.push({ tone: "info", badge: "No calls yet", text: `No calls in ${results.experiment}${where} yet, so every arm shows —.` });
  }
  const sampled = columns.filter((c) => c.data && c.data.users > 0);
  const low = sampled.filter((c) => c.data?.lowSample);
  if (low.length) {
    const names = low.map((c) => (c.key === null ? "the rest" : `arm ${c.label}`)).join(", ");
    const who = low.length === sampled.length ? "Every arm is" : `${names[0].toUpperCase()}${names.slice(1)} ${low.length === 1 ? "is" : "are"}`;
    out.push({
      tone: "warn",
      badge: "Low sample",
      text: `${who} under ${min} users, so the ranges are wide and nothing is marked. Read the direction, not the size.`,
    });
  }
  const liveExperiment = livePolicy(policy, s2sEnabled)?.experiment ?? null;
  if (results.experiment && liveExperiment !== results.experiment) {
    out.push({
      tone: "info",
      badge: "Not live",
      text: !s2sEnabled
        ? "The kill switch is on (VOICE_S2S_ENABLED), so every call runs the cascade outside any experiment and this one gets no calls."
        : policy && !policy.enabled
          ? "The policy is off, so every call runs the cascade outside any experiment and this one gets no calls."
          : liveExperiment
            ? `The servers now run experiment ${liveExperiment}; this one no longer gets calls.`
            : "The policy has no experiment now; this one no longer gets calls.",
    });
  }
  const notAllowed = columns.filter(
    (c) => c.key !== null && c.pct !== null && c.engine && c.engine !== "cascade" && !allowedEngines.includes(c.engine),
  );
  for (const c of notAllowed) {
    out.push({
      tone: "warn",
      badge: "Not allowed here",
      text: `${engineLabel(c.engine as string)} is not allowed on ${environment || "this deployment"}, so arm ${c.label}'s users run the cascade and count under the rest.`,
    });
  }
  if (columns.some((c) => c.engine === "elevenlabs" && !c.data?.calls)) {
    out.push({
      tone: "info",
      badge: "Not built yet",
      text: "The ElevenLabs engine comes with plan B3 and B4. Until then its arms get no calls, and their numbers show —.",
    });
  }
  if (results.language === "vi") {
    out.push({
      tone: "error",
      badge: "Known gap",
      text: "Deepgram Flux, the cascade's speech recognition, does not support Vietnamese, so the cascade does worse here, and every engine's fallback lands on it.",
    });
  }
  if (results.crossArmUsers > 0) {
    out.push({
      tone: "info",
      badge: "Moved arms",
      text: `${plural(results.crossArmUsers, "user")} ran in more than one arm (the split changed during the experiment) and ${results.crossArmUsers === 1 ? "counts" : "count"} in each.`,
    });
  }
  if (results.capped) {
    out.push({ tone: "warn", badge: "Capped", text: "Only the newest calls were read. Narrow the dates to see all of them." });
  }
  return out;
}

// ---------------------------------------------------------------- CSV

const CSV_HEADER = [
  "experiment",
  "language",
  "arm",
  "engine",
  "users",
  "calls",
  "sockets",
  "long_call_rate",
  "long_call_low",
  "long_call_high",
  "median_call_minutes",
  "first_audio_p50_ms",
  "first_audio_p95_ms",
  "interruptions_per_call",
  "talk_overs_per_call",
  "fallback_rate",
  "fallback_reasons",
  "cost_per_minute_usd",
  "positive_sentiment",
  "crisis_judged",
  "crisis_missed_or_harmful",
  "crisis_partial",
  "low_sample",
];

function csvCell(value: unknown): string {
  if (value === null || value === undefined) return "";
  const text = String(value);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

// One row per arm, raw numbers (empty = no data).
export function resultsCsv(results: VoiceEngineResults, columns: ArmColumn[]): string {
  const rows = columns.map((c) => {
    const a = c.data;
    const reasons = a?.fallback ? Object.entries(a.fallback.reasons).map(([r, n]) => `${r}:${n}`).join(";") : null;
    return [
      results.experiment,
      results.language ?? "all",
      c.key ?? "rest",
      c.engine,
      a?.users ?? 0,
      a?.calls ?? 0,
      a?.connections ?? 0,
      a?.longCallRate.value,
      a?.longCallRate.ci?.low,
      a?.longCallRate.ci?.high,
      a?.medianCallMinutes,
      a?.firstAudio.p50Ms,
      a?.firstAudio.p95Ms,
      a?.interruptionsPerCall.value,
      a?.talkOversPerCall.value,
      a?.fallback?.rate.value,
      reasons,
      a?.cost?.perMinute,
      a?.sentiment.positiveShare.value,
      a?.crisis.judged,
      a?.crisis.missedOrHarmful,
      a?.crisis.partial,
      a ? a.lowSample : null,
    ].map(csvCell);
  });
  return [CSV_HEADER, ...rows].map((r) => r.join(",")).join("\n") + "\n";
}
